import { ArrowPathIcon, ClipboardDocumentListIcon } from "@heroicons/react/24/outline";
import type { AppRouter } from "@sp-crm/server/rpc";
import type { inferProcedureOutput } from "@trpc/server";
import { InputFormBlock } from "components/app/input-form-block";
import {
    ActivityRowBody,
    ActivityRowChipLine,
    activityRowIconClasses,
    ActivityRowsList,
} from "components/details-page-v-next/activity-rows";
import {
    isScheduledTaskOverdue,
    sortTasksByDue,
    taskDueLabel,
    taskDueMoment,
} from "components/details-page-v-next/scheduled-activities-rows";
import { Header } from "components/header";
import { BlankSlate } from "components/ui/blank-slate";
import * as React from "react";

export type SharedPlannedActivity = inferProcedureOutput<
    AppRouter["sharedPipeline"]["lead"]
>["plannedActivities"][number];

const SharedPlannedActivityRow: React.FC<{
    activity: SharedPlannedActivity;
    now: Date;
}> = ({ activity, now }) => (
    <li>
        <ActivityRowBody
            leading={
                <ClipboardDocumentListIcon
                    aria-hidden="true"
                    className={activityRowIconClasses}
                />
            }
            actions={null}>
            <span
                className="font-medium text-gray-900"
                data-testid="shared-planned-activity-title">
                {activity.text || "(untitled)"}
            </span>
            <span
                className={
                    isScheduledTaskOverdue(taskDueMoment(activity), now)
                        ? "text-red-600"
                        : "text-gray-500"
                }>
                {" · "}
                {taskDueLabel(activity)}
            </span>
            {activity.taskTypeName && activity.taskTypeCategory ? (
                <ActivityRowChipLine
                    category={activity.taskTypeCategory}
                    label={activity.taskTypeName}
                />
            ) : null}
            {activity.notes ? (
                <div className="text-gray-500 truncate">{activity.notes}</div>
            ) : null}
            <div className="text-xs text-gray-500">
                Assigned to {activity.assigneeName ?? "no one"}
                {activity.recurrencePattern ? (
                    <span className="inline-flex items-center gap-0.5 ml-2">
                        <ArrowPathIcon aria-hidden="true" className="h-3.5 w-3.5" />
                        {activity.recurrencePattern}
                    </span>
                ) : null}
            </div>
        </ActivityRowBody>
    </li>
);

export const SharedPlannedActivities: React.FC<{
    activities: SharedPlannedActivity[];
}> = ({ activities }) => {
    const now = new Date();
    return (
        <InputFormBlock>
            <Header>Planned Activities</Header>
            {activities.length === 0 ? (
                <BlankSlate>Nothing scheduled yet.</BlankSlate>
            ) : (
                <ActivityRowsList className="" testId="shared-planned-activities">
                    {sortTasksByDue(activities).map(activity => (
                        <SharedPlannedActivityRow
                            key={activity.id}
                            activity={activity}
                            now={now}
                        />
                    ))}
                </ActivityRowsList>
            )}
        </InputFormBlock>
    );
};
